import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import {
    Box,
    Card,
    CardContent,
    ListItemText,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Alert,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import { VpnKey as VpnKeyIcon, Add as AddIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import ApiKeyList from './ApiKeyList';
import ApiKeyScopePicker from './ApiKeyScopePicker';
import ConfirmDialog from './admin/ConfirmDialog';
import AppDatePicker from './AppDatePicker';
import { getApiKeyScopes, getMyApiKeys, createApiKey, revokeMyApiKey } from '../redux/ducks/apiKey';
import { showAlert } from '../redux/slices/alerts';

const SettingsCard = styled(Card)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    boxShadow: theme.shadows[2],
}));

const SectionHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
    },
}));

const SectionTitle = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: '1 1 auto',
    minWidth: 0,
}));

const SectionTitleText = styled(ListItemText)(({ theme }) => ({
    '& .MuiListItemText-primary': {
        fontWeight: theme.typography.fontWeightBold,
        fontSize: theme.typography.h6,
        color: theme.palette.text.primary,
        overflowWrap: 'break-word',
    },
    '& .MuiListItemText-secondary': {
        fontWeight: theme.typography.fontWeightMedium,
        fontSize: theme.typography.body2,
        color: theme.palette.text.secondary,
        overflowWrap: 'break-word',
    },
    marginLeft: theme.spacing(2),
    minWidth: 0,
    flex: '1 1 auto',
}));

const DialogHelper = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const FormBlock = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(0.5),
    fontWeight: theme.typography.fontWeightBold,
}));

const NoticeAlert = styled(Alert)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const MonospaceField = styled(TextField)({
    '& .MuiInputBase-root': {
        fontFamily: 'monospace',
        wordBreak: 'break-all',
    },
});

const UsageHint = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(2),
    display: 'block',
}));

const emptyForm = { label: '', scopes: [], expiresAt: '' };

const ApiKeysCard = () => {
    const dispatch = useDispatch();
    const hasApiAccess = useSelector((state) => state.auth.currentUser?.apiAccess === true);
    const [catalogue, setCatalogue] = useState({ domains: [], methods: [], maxScopes: 0 });
    const [apiKeys, setApiKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [busy, setBusy] = useState(false);
    const [formError, setFormError] = useState('');
    const [createdKey, setCreatedKey] = useState(null);
    const [revokeTarget, setRevokeTarget] = useState(null);

    const loadKeys = useCallback(() => {
        setLoading(true);
        dispatch(getMyApiKeys((data) => {
            setApiKeys(Array.isArray(data) ? data : []);
            setLoading(false);
        }));
    }, [dispatch]);

    useEffect(() => {
        dispatch(getApiKeyScopes((data) => {
            if (data?.domains) setCatalogue({ domains: data.domains, methods: data.methods || [], maxScopes: data.maxScopes || 0 });
        }));
        loadKeys();
    }, [dispatch, loadKeys]);

    const openCreate = () => {
        setForm(emptyForm);
        setFormError('');
        setBusy(false);
        setCreateOpen(true);
    };

    const handleCreate = () => {
        const label = form.label.trim();
        if (!label) {
            setFormError('Give the key a name so you can recognise it later.');
            return;
        }
        if (!form.scopes.length) {
            setFormError('Choose at least one thing this key is allowed to do.');
            return;
        }
        setFormError('');
        setBusy(true);
        dispatch(createApiKey({ label, scopes: form.scopes, expiresAt: form.expiresAt || null }, (data, errorMessage) => {
            setBusy(false);
            if (!data?.key) {
                setFormError(errorMessage || 'The key could not be created. Check the details and try again.');
                return;
            }
            setCreateOpen(false);
            setCreatedKey(data.key);
            setForm(emptyForm);
            loadKeys();
        }));
    };

    const handleRevoke = () => {
        if (!revokeTarget) return;
        const target = revokeTarget;
        setRevokeTarget(null);
        dispatch(revokeMyApiKey(target._id, (data) => {
            if (data) dispatch(showAlert({ severity: 'success', message: `"${target.label}" was revoked.` }));
            loadKeys();
        }));
    };

    const copyKey = () => {
        if (!createdKey) return;
        navigator.clipboard?.writeText(createdKey);
        dispatch(showAlert({ severity: 'success', message: 'API key copied to clipboard' }));
    };

    return (
        <SettingsCard>
            <CardContent>
                <SectionHeader>
                    <SectionTitle>
                        <VpnKeyIcon fontSize="medium" />
                        <SectionTitleText
                            primary="API Keys"
                            secondary="Let an outside tool act on your behalf, limited to the areas you allow. A key is shown once when you create it."
                        />
                    </SectionTitle>
                    {hasApiAccess && (
                        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openCreate}>
                            Create API Key
                        </Button>
                    )}
                </SectionHeader>

                {!hasApiAccess && (
                    <NoticeAlert severity="info">
                        API access is switched off for your account, so you cannot create keys. Ask an administrator to turn it on if you need one. Any keys already on your account stay listed below but will not work until it is.
                    </NoticeAlert>
                )}

                <ApiKeyList
                    apiKeys={apiKeys}
                    domains={catalogue.domains}
                    loading={loading}
                    onRevoke={setRevokeTarget}
                    emptyText={hasApiAccess ? 'You have no API keys. Click "Create API Key" to make one for an external tool.' : 'You have no API keys.'}
                />
            </CardContent>

            <Dialog open={createOpen} onClose={busy ? undefined : () => setCreateOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Create an API key</DialogTitle>
                <DialogContent>
                    <DialogHelper variant="body2" color="text.secondary">
                        The key acts as you, so it can only ever reach what you can reach — and only the areas you tick below. The key itself is shown once, right after you create it.
                    </DialogHelper>

                    <FormBlock>
                        <TextField
                            fullWidth
                            autoFocus
                            label="Name"
                            value={form.label}
                            onChange={(e) => setForm((prev) => ({ ...prev, label: e.target.value }))}
                            helperText="Something you will recognise later, e.g. 'Attention triage agent'"
                            disabled={busy}
                            inputProps={{ maxLength: 100 }}
                        />
                    </FormBlock>

                    <FormBlock>
                        <FieldLabel variant="body2">What is this key allowed to do?</FieldLabel>
                        <Typography variant="caption" color="text.secondary">
                            Tick "View" to let it read an area, "Change" to let it add, edit or remove things there. Use the arrow for finer control.
                        </Typography>
                        <ApiKeyScopePicker
                            domains={catalogue.domains}
                            value={form.scopes}
                            onChange={(scopes) => setForm((prev) => ({ ...prev, scopes }))}
                            maxScopes={catalogue.maxScopes}
                            disabled={busy}
                        />
                    </FormBlock>

                    <FormBlock>
                        <AppDatePicker
                            label="Stop working on (optional)"
                            value={form.expiresAt}
                            onChange={(value) => setForm((prev) => ({ ...prev, expiresAt: value }))}
                            minDate={dayjs().add(1, 'day')}
                            helperText="Leave empty for no end date. A key that goes 90 days without being used is disabled automatically."
                            disabled={busy}
                        />
                    </FormBlock>

                    {formError && <Alert severity="error">{formError}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)} disabled={busy}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleCreate}
                        disabled={busy}
                        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {busy ? 'Creating…' : 'Create key'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!createdKey} onClose={() => setCreatedKey(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Your new API key</DialogTitle>
                <DialogContent>
                    <NoticeAlert severity="warning">
                        Copy this key now and store it somewhere safe. It cannot be shown or recovered again — if you lose it, revoke it and create another.
                    </NoticeAlert>
                    <MonospaceField
                        fullWidth
                        multiline
                        label="API key"
                        value={createdKey || ''}
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <Tooltip title="Copy key">
                                    <IconButton size="small" onClick={copyKey}>
                                        <CopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            ),
                        }}
                    />
                    <UsageHint variant="caption" color="text.secondary">
                        Your tool exchanges this key for a short-lived token at POST /api/auth/service/token, then sends that token as an Authorization Bearer header.
                    </UsageHint>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={() => setCreatedKey(null)}>
                        I've copied the key
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={!!revokeTarget}
                onClose={() => setRevokeTarget(null)}
                onConfirm={handleRevoke}
                title="Revoke this API key?"
                confirmLabel="Revoke key"
                message={`"${revokeTarget?.label}" will stop working within 15 minutes and cannot be re-enabled. Any tool using it will lose access.`}
            />
        </SettingsCard>
    );
};

export default ApiKeysCard;
